import {
  APP_ID,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, delay, filter, of, switchMap, take, tap } from 'rxjs';
import { EntryActions } from '../state/entry.actions';
import { EntrySelectors } from '../state/entry.selectors';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { EntryService, MobileView } from './entry.service';
import { EntryData, ProviderData } from './structure-data.models';
import { SummaryData, TedDomain } from '../entry/structure-data.models';
import { CommonService } from '../common.service';
import { GoogleAnalyticsService } from '../google-analytics.service';
import { ConfigService } from '../config.service';
import { PlatformService } from '../platform.service';
import { MolstarPluginService } from '../molstar-plugin-service';
import { ScrollPositionService } from './scroll-position.service';
import { ProviderCacheService } from './ProviderCacheService';
import { UtilService } from '../util.service';

@Component({
  selector: 'app-entry',
  templateUrl: './entry.component.html',
  styleUrls: ['./entry.component.scss'],
  standalone: false,
})
export class EntryComponent implements OnInit {
  public readonly routeTabs = [
    { label: 'Summary and Model Confidence', id: 'summary' },
    { label: 'Macromolecules', id: 'macromolecules' },
    { label: 'Domains', id: 'domains' },
    { label: 'Annotations', id: 'annotations' },
    { label: 'Text Annotations (AI)', id: 'text-annotations' },
    { label: 'Similar Structure', id: 'structures' },
  ];
  private activeTabName = 'summary';
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private store = inject(Store);

  public currentMobileView = this.entryService.mobileView;
  public MobileView = MobileView;

  @ViewChild('tabs') tabGroup!: MatTabGroup;
  public selectedTab = signal<number>(0);
  private sub: any;
  pdbeMolstar: any;
  public summaryData: SummaryData;
  public afdb_accession: string;
  public isSummaryLoaded = false;
  public hasData: number;

  public isMobile = false;

  public csvAMFileUrl: string;
  public accession: string = '';
  public uniprotAccession: string = '';
  public entryData: EntryData;
  public isShowError = this.entryService.showError;
  public isEntryDataAvailable = false;
  public tedDomainData = toSignal(this.store.select(EntrySelectors.tedDomainData));
  public llmAnnotationsData = toSignal(this.store.select(EntrySelectors.llmAnnotationsData));
  public llmAnnotationsLoaded = toSignal(this.store.select(EntrySelectors.llmAnnotationsLoaded));
  // Text Annotations tab chip: spinner until this entry's first llm_annotations response, then
  // decided once — a later per-chain fetch (heterodimer Chain B) never changes it.
  public llmAnnotationsChip = signal<'loading' | 'new' | 'na'>('loading');
  public macromoleculesData = toSignal(this.store.select(EntrySelectors.macromoleculesData));
  public csvAmData = toSignal(this.store.select(EntrySelectors.csvAmData));
  public readonly scrollService = inject(ScrollPositionService);
  public providerData: ProviderData;
  constructor(
    public entryService: EntryService,
    private route: ActivatedRoute,
    public gaService: GoogleAnalyticsService,
    private platform: PlatformService,
    private breakpointObserver: BreakpointObserver,
    private configService: ConfigService,
    private commonService: CommonService,
    private providerCache: ProviderCacheService,
    private utilService: UtilService,
  ) {
    this.hasData = -1;
    effect(() => {
      if (!this.llmAnnotationsLoaded()) {
        this.llmAnnotationsChip.set('loading');
        return;
      }
      if (untracked(this.llmAnnotationsChip) !== 'loading') return;
      this.llmAnnotationsChip.set(untracked(() => this.hasInRangeLlmAnnotations()) ? 'new' : 'na');
    });
    this.route.queryParams.subscribe((params) => {
      this.activeTabName = params['activeTab'] ?? 'summary';
      this.refreshSelectedTabIndex();
      this.breakpointObserver
      .observe([`(max-width: 768px)`])
      .subscribe(result => {
        this.isMobile = result.matches;
      });
    });
  }
  neataccessionStore$ = this.store.select(EntrySelectors.accession);

  ngOnInit(): void {
    this.route.params
      .pipe(
        switchMap((params) => {
          const entryId = params['id'].toLowerCase();
          this.accession = entryId.toUpperCase();
  
          this.store.dispatch(EntryActions.setCurrentAccession({
            accession: this.accession,
          }));
          this.store.dispatch(EntryActions.getEntryData());
  
          // Wait until entryData has the field we care about
          return this.store.select(EntrySelectors.entryData).pipe(
            // adjust predicate to the field that proves data is "ready"
            filter((d): d is EntryData => !!d && typeof d.isUniProt === 'boolean'),
            take(1)
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data) => {
        // now `data` should be the populated entry object (not undefined)
        this.entryData = data;
        this.store.dispatch(EntryActions.setCurrentUniprotAccession({
          uniprotAccession: this.entryData.uniprotAccession
        }));
        this.store.dispatch(EntryActions.setCurrentIsUniprot({
          isUniProt: this.entryData.isUniProt
        }));
  
        this.isEntryDataAvailable = true;
        this.afdb_accession = this.entryData.modelEntityId;
        this.uniprotAccession = this.entryData.uniprotAccession;
        this.store.dispatch(EntryActions.getCSVData());
        this.store.dispatch(EntryActions.getTedDomainData());
        this.store.dispatch(EntryActions.getComplexData());
        this.store.dispatch(EntryActions.getLlmAnnotationsData({}));

        this.refreshSelectedTabIndex();

        if (this.entryData.providerId) {
          this.getProviderData();
        }
  
        if (this.entryData.isUniProt) {
          this.store.dispatch(EntryActions.getUniprotData());
        }

        this.gaService.eventEmitter('entry_dataset_type_ep', 'entry_dataset_view', 'click', `${this.entryData.providerId}`, undefined)
        this.gaService.eventEmitter('entry_dataset_type_ep', 'entry_dataset_view', 'view', `${this.entryData.providerId}`, undefined)
        this.gaService.emitEventWithParams('entry_view', {
          entry_type: this.getEntryTypeForGa(),
          assembly_type: this.getAssemblyTypeForGa(),
        });
      });
  }

  private isMacromoleculesVisible(): boolean {
    return this.entryData?.isComplex === true;
  }

  // Drives the Text Annotations tab's "New"/"N/A" chip. llm_annotations is fetched per
  // UniProt ACCESSION, not per AFDB entry/chain — a polyprotein fragment's entry can get back
  // a non-empty response that's entirely for OTHER fragments of the same protein (AFPDBE-1435-
  // adjacent). So a non-empty response alone doesn't mean this entry itself has annotations;
  // check that at least one returned residue actually falls inside a known chain's own range.
  // For a complex, this reflects whichever chain's data is CURRENTLY loaded (matched by
  // accession against macromoleculesData) — not necessarily the one the user has selected
  // inside the tab right now, since that internal selection isn't exposed up to this
  // component; in the common case (one accession per chain) these coincide.
  public hasInRangeLlmAnnotations(): boolean {
    const loaded = this.llmAnnotationsData();
    if (!loaded?.data?.length) return false;

    if (!this.entryData?.isComplex) {
      const range = this.utilService.getMacromoleculeResidueRange(this.entryData);
      if (!range) return true;
      return loaded.data.some((a) => a.uniprotResidue >= range.start && a.uniprotResidue <= range.end);
    }

    const matchingChains = (this.macromoleculesData() || []).filter(
      (chain: any) => chain?.uniprotAccession && chain.uniprotAccession === loaded.uniprotAccession
    );
    if (matchingChains.length === 0) return true;

    return matchingChains.some((chain) => {
      const range = this.utilService.getMacromoleculeResidueRange(chain);
      if (!range) return true;
      return loaded.data.some((a) => a.uniprotResidue >= range.start && a.uniprotResidue <= range.end);
    });
  }

  private getVisibleRouteTabs() {
    return this.routeTabs.filter((tab) => tab.id !== 'macromolecules' || this.isMacromoleculesVisible());
  }

  private getEntryTypeForGa(): 'monomer' | 'multimer' {
    return this.entryData?.isComplex ? 'multimer' : 'monomer';
  }

  private getAssemblyTypeForGa(): 'homo' | 'hetero' | 'mono' {
    if (!this.entryData?.isComplex) {
      return 'mono';
    }

    const assemblyType = (this.entryData?.assemblyType || '').toLowerCase();
    if (assemblyType === 'homo' || assemblyType === 'hetero') {
      return assemblyType;
    }

    return 'mono';
  }

  private getTabNameForNewGa(tabName: string): 'summary' | 'macromolecules' | 'domains' | 'annotations' | 'text_annotations' | 'similar_proteins' {
    if (tabName === 'structures') {
      return 'similar_proteins';
    }
    if (tabName === 'text-annotations') {
      return 'text_annotations';
    }

    return tabName as 'summary' | 'macromolecules' | 'domains' | 'annotations' | 'text_annotations' | 'similar_proteins';
  }

  private refreshSelectedTabIndex(): void {
    const visibleRouteTabs = this.getVisibleRouteTabs();
    const selectedId = this.activeTabName === 'macromolecules' && !this.isMacromoleculesVisible()
      ? 'domains'
      : this.activeTabName;
    const tabIndex = visibleRouteTabs.findIndex((tab) => tab.id === selectedId);
    this.selectedTab.set(tabIndex >= 0 ? tabIndex : 0);
  }

  public getProviderData(): void {

    this.store.dispatch(EntryActions.getClustermmseqData());
    this.store.dispatch(EntryActions.getClusterfoldseekData());

    const providerId = this.entryData.providerId;
    // 1. Try cached value
    if(this.platform.isBrowserPlatform()){
      const cachedData: ProviderData | null = this.providerCache.get<ProviderData>(providerId);
      if (cachedData) {
        this.providerData = cachedData;
        
        this.store.dispatch(
          EntryActions.setCurrentProvider({
            providerData: this.providerData
          })
        );
        return;
      }

      // 2. Otherwise, call API
      const auth = this.configService.getConfig().apiKey
        ? `?key=${this.configService.getConfig().apiKey}`
        : '';
      const apiUrl =
        this.configService.getConfig().providerApiUrl + providerId + auth;
      this.commonService.getApiData(apiUrl, { isInternal: true }).subscribe(
        (data) => {
          this.providerData = {
            providerName: data.providerName,
            toolUsed: this.entryData.toolUsed,
            providerUrl: data.providerUrl,
            feedback: data.feedback,
            license: data.license,
            citations: data.citations,
            copyrights: data.copyrights,
            modelCreatedDate: this.entryData.modelCreatedDate
          };
          this.store.dispatch(
            EntryActions.setCurrentProvider({
              providerData: this.providerData
            })
          );
          // Save in cache
          this.providerCache.set(providerId, this.providerData);
        },
        (error) => {
          console.error('Failed to fetch provider data:', error);
          this.providerData = {
            providerName : null,
            toolUsed: null,
            providerUrl: null,
            feedback: null,
            license: null,
            citations: null,
            copyrights: null,
            modelCreatedDate: null
          };
          this.store.dispatch(
            EntryActions.setCurrentProvider({
              providerData: this.providerData
            })
          );
          this.providerCache.set(providerId, this.providerData);
        }
      );
    }
  }
  isObjectEmpty(obj: any): boolean {
    return obj && Object.keys(obj).length === 0;
  }

  async selectTab(event: MatTabChangeEvent) {
    const visibleRouteTabs = this.getVisibleRouteTabs();
    const tabName = visibleRouteTabs[event.index]?.id ?? 'summary';
    this.router.navigate([], {
      queryParams: { activeTab: tabName },
      queryParamsHandling: 'merge',
    });
 
    if (this.platform.isBrowserPlatform()) {
      this.scrollService.handleScrollPosition(this.tabGroup, event.index);
    }
    //for GA tags
    let tag = "";
    let gtagmsg = "";
    if(tabName === "summary"){
      tag = "summary_model_confidence";
      gtagmsg = "Clicks on summary tab";
    }
    if(tabName === "domains"){
      tag = "domains_tab";
      gtagmsg = "Clicks on domains tab";
    }
    if(tabName === "macromolecules"){
      tag = "macromolecules_tab";
      gtagmsg = "Clicks on macromolecules tab";
    }
    if(tabName === "annotations"){
      tag = "annotations_tab";
      gtagmsg = "Clicks on annotations tab";
    }
    if(tabName === "text-annotations"){
      tag = "text_annotations_tab";
      gtagmsg = "Clicks on text annotations (AI) tab";
    }
    if(tabName === "structures"){
      tag = "similar_proteins_tab";
      gtagmsg = "Clicks on similar proteins tab";
    }
    this.gaService.eventEmitter('tab_navigation', 'navigation', 'click', tag, gtagmsg);
    this.gaService.emitEventWithParams('tab_nav_new', {
      entry_type: this.getEntryTypeForGa(),
      assembly_type: this.getAssemblyTypeForGa(),
      tab_name: this.getTabNameForNewGa(tabName),
    });
  }
}
